import React, { ReactNode, Fragment } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/useAuthStore';
import { useToastStore } from '@invoiceflow/ui';
import { AuthService } from '../services/AuthService';
import { TenantSwitcher } from './TenantSwitcher';
import { Dialog, Transition, Menu } from '@headlessui/react';
import {
  ArrowPathIcon,
  UsersIcon,
  Cog6ToothIcon,
  ArrowLeftOnRectangleIcon,
  Squares2X2Icon,
  BuildingOfficeIcon,
  ChatBubbleBottomCenterTextIcon,
  DocumentTextIcon,
  CreditCardIcon,
  BanknotesIcon,
  ChartBarIcon,
  PresentationChartLineIcon,
  ArrowTrendingUpIcon,
  XMarkIcon,
  Bars3Icon,
  BellIcon,
  UserCircleIcon,
  ChevronDownIcon,
  QuestionMarkCircleIcon,
  CalculatorIcon,
} from '@heroicons/react/24/outline';

interface LayoutProps {
  children: ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [isSidebarOpen, setIsSidebarOpen] = React.useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, isInitialized } = useAuthStore();
  const { addToast } = useToastStore();

  // Extract tenantId from pathname if we are in a tenant scope
  // Pattern: /tenants/:tenantId/...
  const tenantMatch = location.pathname.match(/\/tenants\/([^/]+)/);
  const currentPathTenantId = tenantMatch ? tenantMatch[1] : undefined;

  const handleLogout = async () => {
    // Best-effort server-side revocation of both tokens; clear locally regardless.
    try {
      await AuthService.logout();
    } catch {
      // ignore network/401 errors on logout
    }
    logout();
    navigate('/login');
  };

  // The tenant whose pages are being viewed: the one in the URL, or — for
  // admins only — their own. A superadmin's own tenantId is 'system', not a
  // real tenant, so outside /tenants/:id/* they have no tenant scope and the
  // tenant-scoped items below stay hidden (visible once they drill in, which
  // matches the API's superadmin access).
  const tenantScope = currentPathTenantId || (user?.role === 'admin' ? user.tenantId : undefined);

  // Superadmins pick their tenant from the sidebar; admins are always scoped to
  // their own, so the switcher would be a one-item menu for them.
  const showTenantSwitcher = user?.role === 'superadmin';
  const sectionMatch = location.pathname.match(/^\/tenants\/[^/]+\/([^/]+)/);
  const currentSection = sectionMatch ? sectionMatch[1] : undefined;

  const navigation = [
    {
      name: 'Dashboard',
      href: tenantScope
        ? `/tenants/${tenantScope}`
        : user?.role === 'superadmin'
          ? '/tenants'
          : `/`,
      icon: Squares2X2Icon,
      roles: ['admin', 'superadmin'],
    },
    { name: 'Tenants List', href: '/tenants', icon: BuildingOfficeIcon, roles: ['superadmin'] },
    {
      name: 'Global Audit',
      href: '/global-comms',
      icon: ChatBubbleBottomCenterTextIcon,
      roles: ['superadmin'],
    },
    {
      name: 'Customers',
      href: `/tenants/${tenantScope}/customers`,
      icon: UsersIcon,
      roles: ['admin', 'superadmin'],
      hidden: !tenantScope,
    },
    {
      name: 'Invoices',
      href: `/tenants/${tenantScope}/invoices`,
      icon: DocumentTextIcon,
      roles: ['admin', 'superadmin'],
      hidden: !tenantScope,
    },
    {
      name: 'Payments',
      href: `/tenants/${tenantScope}/payments`,
      icon: CreditCardIcon,
      roles: ['admin', 'superadmin'],
      hidden: !tenantScope,
    },
    {
      name: 'Expenses',
      href: `/tenants/${tenantScope}/expenses`,
      icon: BanknotesIcon,
      roles: ['admin', 'superadmin'],
      hidden: !tenantScope,
    },
    {
      name: 'Subscriptions',
      href: `/tenants/${tenantScope}/subscriptions`,
      icon: ArrowPathIcon,
      roles: ['admin', 'superadmin'],
      hidden: !tenantScope,
    },
    {
      name: 'Tax',
      href: `/tenants/${tenantScope}/tax`,
      icon: CalculatorIcon,
      roles: ['admin', 'superadmin'],
      hidden: !tenantScope,
    },
    {
      name: 'Performance',
      href: `/tenants/${tenantScope}/performance`,
      icon: ChartBarIcon,
      roles: ['admin', 'superadmin'],
      hidden: !tenantScope,
    },
    {
      name: 'Analysis',
      href: `/tenants/${tenantScope}/analysis`,
      icon: PresentationChartLineIcon,
      roles: ['admin', 'superadmin'],
      hidden: !tenantScope,
    },
    {
      name: 'Cash Flow',
      href: `/tenants/${tenantScope}/projection`,
      icon: ArrowTrendingUpIcon,
      roles: ['admin', 'superadmin'],
      hidden: !tenantScope,
    },
    { name: 'My Settings', href: '/settings', icon: Cog6ToothIcon, roles: ['admin', 'superadmin'] },
    { name: 'Help', href: '/help', icon: QuestionMarkCircleIcon, roles: ['admin', 'superadmin'] },
  ];

  const filteredNavigation =
    !isInitialized || !user
      ? []
      : navigation.filter(item => item.roles.includes(user.role) && !item.hidden);

  const isActive = (path: string) => {
    if (path === '/' && location.pathname !== '/') return false;
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Mobile sidebar */}
      <Transition.Root show={isSidebarOpen} as={Fragment}>
        <Dialog as="div" className="relative z-40 lg:hidden" onClose={setIsSidebarOpen}>
          <Transition.Child
            as={Fragment}
            enter="transition-opacity ease-linear duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-600 bg-opacity-75" />
          </Transition.Child>

          <div className="fixed inset-0 z-40 flex">
            <Transition.Child
              as={Fragment}
              enter="transition ease-in-out duration-300 transform"
              enterFrom="-translate-x-full"
              enterTo="translate-x-0"
              leave="transition ease-in-out duration-300 transform"
              leaveFrom="translate-x-0"
              leaveTo="-translate-x-full"
            >
              <Dialog.Panel className="relative flex w-full max-w-xs flex-1 flex-col bg-white">
                <Transition.Child
                  as={Fragment}
                  enter="ease-in-out duration-300"
                  enterFrom="opacity-0"
                  enterTo="opacity-100"
                  leave="ease-in-out duration-300"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <div className="absolute top-0 right-0 -mr-12 pt-2">
                    <button
                      type="button"
                      className="ml-1 flex h-10 w-10 items-center justify-center rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                      onClick={() => setIsSidebarOpen(false)}
                    >
                      <span className="sr-only">Close sidebar</span>
                      <XMarkIcon className="h-6 w-6 text-white" aria-hidden="true" />
                    </button>
                  </div>
                </Transition.Child>
                <div className="flex flex-shrink-0 items-center px-4 h-16 bg-indigo-600">
                  <span className="text-xl font-bold text-white">InvoiceFlow</span>
                </div>
                {showTenantSwitcher && (
                  <div className="mt-5">
                    <TenantSwitcher
                      currentTenantId={currentPathTenantId}
                      section={currentSection}
                      onNavigate={() => setIsSidebarOpen(false)}
                    />
                  </div>
                )}
                <div className="mt-5 h-0 flex-1 overflow-y-auto">
                  <nav className="space-y-1 px-2">
                    {filteredNavigation.map(item => (
                      <Link
                        key={item.name}
                        to={item.href}
                        onClick={() => setIsSidebarOpen(false)}
                        className={`${
                          isActive(item.href)
                            ? 'bg-indigo-50 text-indigo-600'
                            : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                        } group flex items-center px-2 py-2 text-base font-medium rounded-md`}
                      >
                        <item.icon className="mr-4 h-6 w-6" />
                        {item.name}
                      </Link>
                    ))}
                  </nav>
                </div>
              </Dialog.Panel>
            </Transition.Child>
            <div className="w-14 flex-shrink-0"></div>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Desktop sidebar */}
      <div className="hidden lg:flex lg:w-64 lg:flex-col lg:fixed lg:inset-y-0">
        <div className="flex flex-col flex-grow bg-white border-r border-gray-200 pt-5 pb-4">
          <div className="flex items-center flex-shrink-0 px-4">
            <span className="text-2xl font-bold text-indigo-600">InvoiceFlow</span>
          </div>
          {showTenantSwitcher && (
            <div className="mt-6">
              <TenantSwitcher currentTenantId={currentPathTenantId} section={currentSection} />
            </div>
          )}
          <nav className="mt-6 flex-1 px-2 space-y-1 overflow-y-auto">
            {filteredNavigation.map(item => (
              <Link
                key={item.name}
                to={item.href}
                className={`${
                  isActive(item.href)
                    ? 'bg-indigo-50 text-indigo-600'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                } group flex items-center px-2 py-2 text-sm font-medium rounded-md`}
              >
                <item.icon className="mr-3 h-5 w-5" />
                {item.name}
              </Link>
            ))}
          </nav>
          <div className="flex-shrink-0 flex border-t border-gray-200 p-4">
            <button
              onClick={handleLogout}
              className="flex-shrink-0 w-full group block text-gray-600 hover:text-gray-900"
            >
              <div className="flex items-center">
                <ArrowLeftOnRectangleIcon className="inline-block h-5 w-5 mr-3" />
                <div className="text-sm font-medium">Logout</div>
              </div>
            </button>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-64 flex flex-col flex-1">
        <div className="sticky top-0 z-10 flex-shrink-0 flex h-16 bg-white shadow">
          <button
            type="button"
            className="px-4 border-r border-gray-200 text-gray-500 lg:hidden"
            onClick={() => setIsSidebarOpen(true)}
          >
            <Bars3Icon className="h-6 w-6" />
          </button>
          <div className="flex-1 px-4 flex justify-between">
            <div className="flex-1 flex items-center">
              <h1 className="text-xl font-semibold text-gray-900">
                {filteredNavigation.find(n => isActive(n.href))?.name || 'Dashboard'}
              </h1>
            </div>
            <div className="ml-4 flex items-center md:ml-6">
              <button
                type="button"
                className="p-1 rounded-full text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                onClick={() => addToast('No new notifications', 'info')}
              >
                <BellIcon className="h-6 w-6" />
              </button>

              <Menu as="div" className="relative ml-3">
                <div>
                  <Menu.Button className="flex max-w-xs items-center rounded-full bg-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2">
                    <span className="sr-only">Open user menu</span>
                    <span className="inline-block h-8 w-8 rounded-full overflow-hidden bg-gray-100">
                      <UserCircleIcon className="h-full w-full text-gray-300" />
                    </span>
                    <div className="ml-3 text-left hidden sm:block">
                      <p className="text-sm font-medium text-gray-900">{user?.name || 'User'}</p>
                      <p className="text-xs text-gray-500">{user?.role}</p>
                    </div>
                    <ChevronDownIcon className="ml-2 h-4 w-4 text-gray-400" />
                  </Menu.Button>
                </div>
                <Transition
                  as={Fragment}
                  enter="transition ease-out duration-100"
                  enterFrom="transform opacity-0 scale-95"
                  enterTo="transform opacity-100 scale-100"
                  leave="transition ease-in duration-75"
                  leaveFrom="transform opacity-100 scale-100"
                  leaveTo="transform opacity-0 scale-95"
                >
                  <Menu.Items className="absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                    <Menu.Item>
                      {({ active }) => (
                        <Link
                          to="/settings"
                          className={`${
                            active ? 'bg-gray-100' : ''
                          } block px-4 py-2 text-sm text-gray-700`}
                        >
                          Settings
                        </Link>
                      )}
                    </Menu.Item>
                    <Menu.Item>
                      {({ active }) => (
                        <button
                          onClick={handleLogout}
                          className={`${
                            active ? 'bg-gray-100' : ''
                          } block w-full text-left px-4 py-2 text-sm text-gray-700`}
                        >
                          Logout
                        </button>
                      )}
                    </Menu.Item>
                  </Menu.Items>
                </Transition>
              </Menu>
            </div>
          </div>
        </div>

        <main className="flex-1">
          <div className="py-6">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">{children}</div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default Layout;
